/**
 * Shared pieces of the dispatch screens (OneFleet WP-U5/U6): the order status badge and the workflow pipeline,
 * the company hook the fleet screens share, and the small formatters (distance, duration, relative time).
 */
import { useTranslation } from "react-i18next";
import { useOneFleetMe } from "../../lib/onefleet/queries";

/** The signed-in person's company and permissions. A whole-group administrator (every company, none selected —
 * `company_public_id` null on the bare `/me`) gets the first membership as the company and **the permissions of
 * that company** from a second, scoped `/me` — without it `can()` was false for the realm's administrators. */
export function useFleetCompany() {
  const me = useOneFleetMe();
  const company = me.data?.company_public_id ?? me.data?.memberships[0]?.company_public_id ?? undefined;
  const needsScope = !!me.data && !me.data.company_public_id && !!company;
  const scoped = useOneFleetMe(needsScope, needsScope ? company : undefined);
  const permissions = (needsScope ? scoped.data?.permissions : me.data?.permissions) ?? [];
  const can = (perm: string) => permissions.includes(perm) || permissions.includes("*");
  return { me, company, can };
}

export const STATUS_CLASS: Record<string, string> = {
  created: "bg-brand-grey-light text-brand-charcoal",
  dispatched: "bg-sky-100 text-sky-900",
  started: "bg-blue-100 text-blue-900",
  picked_up: "bg-indigo-100 text-indigo-900",
  en_route: "bg-violet-100 text-violet-900",
  completed: "bg-green-100 text-green-800",
  canceled: "bg-red-100 text-red-800",
};

export function OrderStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[status] ?? STATUS_CLASS.created}`} data-fleet-order-status={status}>
      {t(`fleet.orders.status.${status}`, { defaultValue: status })}
    </span>
  );
}

export function BillingBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cls = status === "paid" ? "bg-green-100 text-green-800" : status === "invoiced" ? "bg-sky-100 text-sky-900" : status === "draft" ? "bg-amber-100 text-amber-900" : "bg-brand-grey-light text-brand-grey";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{t(`fleet.orders.billing.${status}`, { defaultValue: status })}</span>;
}

/** The workflow's states in order with the current one highlighted (wireframe W-04 ①). */
export function Pipeline({ states, current }: { states: string[]; current: string }) {
  const { t } = useTranslation();
  const idx = states.indexOf(current);
  const canceled = current === "canceled";
  return (
    <ol className="flex flex-wrap items-center gap-1" data-fleet-pipeline={current}>
      {states.map((s, i) => {
        const done = idx >= 0 && i < idx;
        const now = s === current;
        const cls = canceled ? "bg-brand-grey-light text-brand-grey line-through" : now ? "bg-brand-primary text-white" : done ? "bg-green-100 text-green-800" : "bg-brand-grey-light text-brand-grey";
        return (
          <li key={s} className="flex items-center gap-1">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{t(`fleet.orders.status.${s}`, { defaultValue: s })}</span>
            {i < states.length - 1 && <span className="text-brand-grey-light">›</span>}
          </li>
        );
      })}
      {canceled && <li><span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">{t("fleet.orders.status.canceled")}</span></li>}
    </ol>
  );
}

export function fmtDistance(m: number | null | undefined, locale: string): string {
  if (m === null || m === undefined) return "—";
  return m >= 1000 ? `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(m / 1000)} km` : `${Math.round(m)} m`;
}

export function fmtDuration(s: number | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h} h ${m.toString().padStart(2, "0")}` : `${m} min`;
}

export function fmtWhen(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return d.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
}

export function fmtDateTime(iso: string | null | undefined, locale: string): string {
  return iso ? new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }) : "—";
}

export function orderLabel(o: { internal_id: string | null; id: string; tracking_number?: string | { number: string } | null }): string {
  const tn = typeof o.tracking_number === "string" ? o.tracking_number : o.tracking_number?.number;
  return o.internal_id ?? tn ?? o.id;
}
